/**
 * ALTIORA · Le Manifeste — Positionnement officiel « Intelligence Décisionnelle ».
 *
 * Page publique standalone (SEO-first) qui affirme l'identité de la
 * plateforme. Le ton est déclaratif, sans jargon, sans emoji. Elle sert de
 * référence dans les prises de parole, les campagnes et les demandes de
 * démonstration.
 */
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, Download } from "lucide-react";
import { AltioraTreeLogo } from "@/components/brand/AltioraTreeLogo";
import { useDefaultLocation } from "@/hooks/useUserLocation";

// ─── Design tokens (aligned with Landing.jsx) ────────────────────────
const NAVY = "#0B2545";
const NAVY_DEEP = "#061528";
const CHAMPAGNE = "#C5A880";
const GOLD = "#B98F3D";
const IVORY = "#FBF6EA";
const CANVAS = "#FCFBF7";
const HAIRLINE = "#E8E1D0";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const MUTED = "#64748B";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

function Eyebrow({ children }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-px" style={{ background: CHAMPAGNE }} />
      <div
        className="font-mono text-[10px] uppercase tracking-[0.32em]"
        style={{ color: CHAMPAGNE }}
      >
        {children}
      </div>
    </div>
  );
}

export default function Manifeste() {
  const { label: locationLabel } = useDefaultLocation();
  const pressKitUrl = `${process.env.REACT_APP_BACKEND_URL}/api/manifeste/press-kit.pdf`;

  // ── SEO — document title + meta description ────────────────────────
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Le Manifeste — ALTIORA · Intelligence Décisionnelle";
    const metas = [];
    const set = (name, content, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      metas.push({ el, created });
    };
    set("description",
        "ALTIORA n'est pas un CRM, ni un assistant, ni un simple logiciel. ALTIORA est une Intelligence Décisionnelle conçue pour les professionnels, les organisations et les institutions.");
    set("og:title", "Le Manifeste — ALTIORA · Intelligence Décisionnelle", true);
    set("og:description",
        "La première Intelligence Décisionnelle pour les professionnels. Positionnement officiel d'ALTIORA.", true);
    set("og:type", "website", true);
    return () => {
      document.title = prevTitle;
      metas.forEach(({ el, created }) => created && el.remove());
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: CANVAS, color: INK }} data-testid="manifeste-root">
      {/* ── Minimal nav ─────────────────────────────────────────────── */}
      <nav
        className="sticky top-0 z-30 backdrop-blur-md border-b"
        style={{ background: `${CANVAS}CC`, borderColor: HAIRLINE }}
        data-testid="manifeste-nav"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="manifeste-brand">
            <AltioraTreeLogo size={22} />
            <span className="font-serif text-[15px] tracking-tight" style={{ color: INK }}>
              ALTIORA
            </span>
          </Link>
          <div className="flex items-center gap-6 text-[13px]">
            <Link to="/" style={{ color: INK_SOFT }} data-testid="manifeste-home-link">Accueil</Link>
            <Link to="/demo" style={{ color: INK_SOFT }} data-testid="manifeste-demo-link">Démonstration</Link>
            <a
              href={pressKitUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="manifeste-press-kit-link"
              className="inline-flex items-center gap-1.5"
              style={{ color: GOLD }}
            >
              <Download size={13} strokeWidth={2} /> Kit presse
            </a>
            <Link to="/login" style={{ color: INK_SOFT }} data-testid="manifeste-login-link">Se connecter</Link>
          </div>
        </div>
      </nav>

      {/* ── Hero — declaration ─────────────────────────────────────── */}
      <section className="relative overflow-hidden" data-testid="manifeste-hero">
        <div
          aria-hidden
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.06] blur-3xl pointer-events-none"
          style={{ background: `radial-gradient(circle, ${GOLD}, transparent 70%)` }}
        />
        <div className="max-w-4xl mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-16 md:pb-24 relative">
          <motion.div {...fadeUp}>
            <Eyebrow>
              Le Manifeste{locationLabel ? ` · Depuis ${locationLabel}` : ""}
            </Eyebrow>
          </motion.div>

          <motion.h1
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="mt-8 font-serif leading-[1.04] tracking-tight"
            style={{
              color: INK,
              fontSize: "clamp(2.4rem, 6vw, 4.8rem)",
              fontWeight: 400,
            }}
            data-testid="manifeste-title"
          >
            Plus qu'une intelligence
            <br />
            artificielle.
            <br />
            <span style={{ fontStyle: "italic", color: GOLD }}>
              Une Intelligence Décisionnelle.
            </span>
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-10 max-w-2xl text-[17px] md:text-[19px] leading-[1.7]"
            style={{ color: INK_SOFT }}
          >
            La première Intelligence Décisionnelle conçue pour les
            professionnels, les organisations et les institutions.
          </motion.p>
        </div>
      </section>

      {/* ── Declarations — what ALTIORA is not ─────────────────────── */}
      <section
        className="border-t"
        style={{ borderColor: HAIRLINE, background: NAVY_DEEP, color: IVORY }}
        data-testid="manifeste-declarations"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-36">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-px" style={{ background: CHAMPAGNE }} />
              <div className="font-mono text-[10px] uppercase tracking-[0.32em]"
                   style={{ color: CHAMPAGNE }}>
                Trois affirmations
              </div>
            </div>
          </motion.div>

          <div className="mt-10 space-y-10">
            {[
              { negation: "n'est pas un assistant", explanation: "Un assistant obéit. Une intelligence propose, arbitre et projette." },
              { negation: "n'est pas un CRM", explanation: "Un CRM enregistre le passé. ALTIORA anticipe la trajectoire." },
              { negation: "n'est pas un simple logiciel", explanation: "Un logiciel exécute des tâches. Une intelligence transforme les décisions." },
            ].map((row, i) => (
              <motion.div
                key={row.negation}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.1 + i * 0.08 }}
                data-testid={`manifeste-declaration-${i}`}
                className="pb-10"
                style={{ borderBottom: i < 2 ? `1px solid ${CHAMPAGNE}22` : "none" }}
              >
                <div className="font-serif tracking-tight"
                     style={{ fontSize: "clamp(1.6rem, 3.4vw, 2.4rem)", color: IVORY, fontWeight: 400, lineHeight: 1.15 }}>
                  ALTIORA <span style={{ color: `${IVORY}88` }}>{row.negation}.</span>
                </div>
                <div className="mt-3 text-[15px] md:text-[16px] leading-[1.7]"
                     style={{ color: `${IVORY}AA`, maxWidth: 640 }}>
                  {row.explanation}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.4 }}
            className="mt-16"
          >
            <div className="font-serif italic tracking-tight"
                 style={{ fontSize: "clamp(2rem, 4.4vw, 3.2rem)", color: GOLD, fontWeight: 400, lineHeight: 1.1 }}
                 data-testid="manifeste-affirmation">
              ALTIORA est une Intelligence Décisionnelle.
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Explanation — what decision intelligence means ─────────── */}
      <section
        className="border-t"
        style={{ borderColor: HAIRLINE, background: CANVAS }}
        data-testid="manifeste-explanation"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <motion.div {...fadeUp}>
            <Eyebrow>Définition</Eyebrow>
          </motion.div>

          <motion.h2
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="mt-8 font-serif leading-[1.1] tracking-tight"
            style={{ color: INK, fontSize: "clamp(1.8rem, 3.8vw, 2.6rem)", fontWeight: 400 }}
          >
            Une intelligence qui ne se contente pas de répondre.
            <br />
            <span style={{ fontStyle: "italic", color: GOLD }}>
              Elle décide avec vous.
            </span>
          </motion.h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-10">
            {[
              { k: "01", label: "Observer",
                text: "Elle capte des signaux invisibles : opportunités, risques, moments-clés." },
              { k: "02", label: "Arbitrer",
                text: "Elle compare des scénarios et mesure l'impact de chaque décision." },
              { k: "03", label: "Projeter",
                text: "Elle rend visible la trajectoire — d'ici trois mois, d'ici trois ans." },
            ].map((row, i) => (
              <motion.div
                key={row.k}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.15 + i * 0.08 }}
                data-testid={`manifeste-pillar-${row.k}`}
              >
                <div className="font-mono text-[11px] tracking-[0.22em]" style={{ color: CHAMPAGNE }}>
                  {row.k}
                </div>
                <div className="mt-3 font-serif text-[22px] tracking-tight" style={{ color: INK }}>
                  {row.label}
                </div>
                <div className="mt-3 text-[15px] leading-[1.7]" style={{ color: INK_SOFT }}>
                  {row.text}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── For whom ───────────────────────────────────────────────── */}
      <section
        className="border-t"
        style={{ borderColor: HAIRLINE, background: IVORY }}
        data-testid="manifeste-audience"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <motion.div {...fadeUp}>
            <Eyebrow>Pour qui</Eyebrow>
          </motion.div>

          <motion.h2
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="mt-8 font-serif leading-[1.1] tracking-tight"
            style={{ color: INK, fontSize: "clamp(1.8rem, 3.8vw, 2.6rem)", fontWeight: 400 }}
          >
            Trois publics. Une même exigence.
          </motion.h2>

          <div className="mt-14 space-y-8">
            {[
              { label: "Les professionnels",
                text: "Ceux dont chaque journée est faite de décisions à haute valeur : conseillers, consultants, cliniciens, dirigeants." },
              { label: "Les organisations",
                text: "Cabinets, cliniques, cellules d'affaires — dès qu'un collectif orchestre des décisions à répétition." },
              { label: "Les institutions",
                text: "Assureurs, coopératives, régulateurs — celles pour qui la qualité des décisions engage la confiance." },
            ].map((row, i) => (
              <motion.div
                key={row.label}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.15 + i * 0.08 }}
                data-testid={`manifeste-audience-${i}`}
                className="flex items-start gap-6 py-4"
                style={{ borderBottom: i < 2 ? `1px solid ${HAIRLINE}` : "none" }}
              >
                <div className="font-mono text-[11px] tracking-[0.22em] pt-1 min-w-[3ch]"
                     style={{ color: CHAMPAGNE }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <div className="font-serif text-[22px] tracking-tight" style={{ color: INK }}>
                    {row.label}
                  </div>
                  <div className="mt-1 text-[15px] leading-[1.7]" style={{ color: INK_SOFT }}>
                    {row.text}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Ambition ───────────────────────────────────────────────── */}
      <section
        className="border-t"
        style={{ borderColor: HAIRLINE, background: NAVY_DEEP, color: IVORY }}
        data-testid="manifeste-ambition"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-36 text-center">
          <motion.div {...fadeUp} className="inline-flex items-center gap-3">
            <div className="w-8 h-px" style={{ background: CHAMPAGNE }} />
            <div className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: CHAMPAGNE }}>
              Ambition
            </div>
            <div className="w-8 h-px" style={{ background: CHAMPAGNE }} />
          </motion.div>

          <motion.h2
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="mt-8 font-serif leading-[1.1] tracking-tight"
            style={{ color: IVORY, fontSize: "clamp(2rem, 5vw, 3.6rem)", fontWeight: 400 }}
          >
            Devenir la référence mondiale
            <br />
            <span style={{ fontStyle: "italic", color: GOLD }}>
              de l'intelligence décisionnelle.
            </span>
          </motion.h2>

          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-10 text-[16px] md:text-[17px] leading-[1.8] mx-auto max-w-2xl"
            style={{ color: `${IVORY}CC` }}
          >
            Que chaque décision professionnelle, où qu'elle se prenne dans
            le monde, puisse s'appuyer sur une intelligence qui l'éclaire,
            l'arbitre et la projette.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.3 }}
            className="mt-12 flex items-center justify-center gap-4 flex-wrap"
          >
            <Link
              to="/demo"
              data-testid="manifeste-cta-demo"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-medium transition"
              style={{ background: IVORY, color: NAVY }}
            >
              Demander une démonstration <ArrowRight size={14} />
            </Link>
            <a
              href={pressKitUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="manifeste-cta-press-kit"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-medium transition"
              style={{ border: `1px solid ${CHAMPAGNE}66`, color: IVORY }}
            >
              <Download size={14} /> Télécharger le kit presse
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────── */}
      <footer
        className="border-t"
        style={{ borderColor: HAIRLINE, background: CANVAS, color: MUTED }}
        data-testid="manifeste-footer"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <AltioraTreeLogo size={22} />
            <span className="font-serif text-[15px] tracking-tight" style={{ color: INK }}>
              ALTIORA
            </span>
          </div>
          <div className="text-[12px]">
            © {new Date().getFullYear()} ALTIORA Coop{locationLabel ? ` · ${locationLabel}` : ""} · Intelligence Décisionnelle pour les professionnels.
          </div>
          <div className="flex items-center gap-6 text-[13px]">
            <Link to="/" style={{ color: INK_SOFT }}>Accueil</Link>
            <a href="mailto:hello@altioracoop.com" style={{ color: INK_SOFT }}>Nous contacter</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
